<template>
  <div :class="containerClass">
    <page-header
      title="Configure FAQs"
      :container-class="null"
      class="mb-16pt"
      :info-text="
        getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL
          ? `Seamlessly manage and update your site's Frequently Asked Questions page.`
          : ''
      "
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group
        :label="$t('schoolMsgs.school')"
        label-for="school"
        label-class="form-label"
        v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN"
      >
        <v-select
          id="school"
          class="form-control v-select-custom"
          label="name"
          v-model="school"
          :reduce="(school) => school.id"
          placeholder="Select School"
          :options="allSchools"
          :loading="areSchoolsLoading"
          @input="prefillLogo(school)"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <page-layout-picker page-key="faqs" :school-id="school" />

      <page-separator title="Manage FAQs" />

      <div class="d-flex flex-wrap justify-content-end">
        <b-btn
          variant="primary"
          :to="{ name: 'add-faq', query: { ...(school && { school_id: school }) } }"
          style="width: 150px"
          type="submit"
          class="btn-normal mb-2"
        >
          <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>Add</span>
        </b-btn>
      </div>

      <div class="row mb-lg-12pt" v-if="campuses.length">
        <draggable class="col-sm-12 draggable-cursor" @start="dragging = true" @end="updateOrder" :list="campuses">
          <div v-for="(item, index) in campuses" :key="index">
            <campus-card
              :name="item.question"
              :linkedWebsite="item.linked_website"
              :isEnable="item.is_enabled"
              :index="item.id"
              @removeCampus="removeCampus"
              @updateIsEnable="updateIsEnable"
            />
          </div>
        </draggable>
      </div>
      <b-card
        v-else-if="
          (get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN && school) ||
          get(getLoggedInUser, 'role') !== USER_ROLES.SUPER_ADMIN
        "
        class="text-center"
      >
        <i> There isn't any custom faqs added. Please add if you have any. </i>
      </b-card>
      <b-form-invalid-feedback
        class="mb-lg-12pt"
        v-if="(!$v.campuses.required || !$v.campuses.minLength) && $v.campuses.$dirty"
        :state="(!$v.campuses.required || !$v.campuses.minLength) && $v.campuses.$dirty ? false : null"
        >There isn't any faq added. Please add a faq if you have any.</b-form-invalid-feedback
      >

      <!-- Page Style -->
      <page-separator title="Page Style" />
      <section-style-panel
        v-model="pageStyle"
        :capabilities="PAGE_STYLE_CAPABILITIES.faqs"
        :organization="selectedOrganization"
      />
      <div class="d-flex justify-content-end mt-3">
        <b-btn variant="primary" type="button" class="btn-normal" :disabled="isPageStyleSaving" @click="savePageStyle">
          <b-spinner small v-if="isPageStyleSaving" class="mr-2" />
          <span>{{ isPageStyleSaving ? 'Saving' : 'Save Page Style' }}</span>
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<style lang="css" scoped>
</style>

<script>
import { get, isEmpty, map } from 'lodash';

import { mapGetters, mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { FmvAvatar } from 'fmv-avatar';
import Page from '@/components/Page.vue';
import { USER_ROLES, USER_ROLE_TYPES, SCHOOL_PAYMENT_PLANS } from '@/common/constants';
import { requiredIf, required, minLength } from 'vuelidate/lib/validators';
import draggable from 'vuedraggable';
import CampusCard from './Cards/FaqCard.vue';
import vSelect from 'vue-select';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import SectionStylePanel, { SECTION_STYLE_DEFAULTS } from '@/views/SuperAdminPortal/WebsiteConfig/SectionStylePanel.vue';
import { PAGE_STYLE_CAPABILITIES } from '@/components/WebsiteConfig/pageStyleMixin';
import PageLayoutPicker from '@/components/WebsiteConfig/PageLayoutPicker.vue';

export default {
  components: {
    PageLayoutPicker,
    PageHeader,
    FmvAvatar,
    draggable,
    CampusCard,
    vSelect,
    PageSeparator,
    SectionStylePanel,
  },
  extends: Page,
  data() {
    return {
      campuses: [],

      SCHOOL_PAYMENT_PLANS,
      title: 'Configure FAQs',
      linkedWebsite: null,
      school: null,
      dragging: false,
      isEnable: true,
      options: [
        { item: 'stats', name: 'Stats' },
        { item: 'feature', name: 'Feature' },
      ],

      areSchoolsLoading: false,
      allSchools: [],
      isLoading: false,

      USER_ROLES,
      USER_ROLE_TYPES,
      PAGE_STYLE_CAPABILITIES,
      pageStyle: { ...SECTION_STYLE_DEFAULTS },
      isPageStyleSaving: false,
    };
  },
  validations() {
    return {
      campuses: { required, minLength: minLength(1) },
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN),
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isFormLoading() {
      return this.isLoading || this.areSchoolsLoading;
    },
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },
    selectedOrganization() {
      if (this.isSuperAdmin) {
        return (this.allSchools || []).find((sch) => sch.id === this.school);
      }
      return get(this.getLoggedInUser, 'linked_entity');
    },
  },

  methods: {
    ...mapActions('school', ['getAllSchools', 'getSchool']),
    ...mapActions('webConfig', ['getFaqs', 'updateFaqsOrder', 'deleteFaq', 'updateFaq', 'getBasicConfig', 'updatePageStyle']),
    get,
    isEmpty,

    async fetchPageStyle() {
      try {
        const params = this.isSuperAdmin ? { school_id: this.school } : {};
        if (this.isSuperAdmin && !this.school) return;
        const config = await this.getBasicConfig(params);
        this.pageStyle = { ...SECTION_STYLE_DEFAULTS, ...(get(config, 'faqs_page_style') || {}) };
      } catch (e) {
        // silent fail to avoid blocking page load
      }
    },

    async savePageStyle() {
      if (this.isSuperAdmin && !this.school) {
        this.makeToast({ variant: 'warning', msg: 'Please select a school first.' });
        return;
      }
      this.isPageStyleSaving = true;
      try {
        await this.updatePageStyle({
          school_id: this.isSuperAdmin ? this.school : null,
          faqs_page_style: this.pageStyle,
        });
        this.makeToast({ variant: 'success', msg: 'FAQs page style updated!' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isPageStyleSaving = false;
      }
    },

    async updateIsEnable(res) {
      const data = {
        linked_website: res.linkedWebsite,
        is_enabled: res.isEnable,
        school:
          get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
            ? this.school
            : this.getLoggedInUser.linked_entity.id,
      };
      await this.updateFaq({
        id: res.index,
        data,
      });
      this.makeToast({ variant: 'success', msg: "Faq's Status Updated!" });
    },

    prefillLogo(schoolId) {
      if (schoolId) {
        this.$router.replace({ name: 'faqs', query: { school_id: this.school } });
        this.fetchBasicConfig(schoolId);
        this.fetchPageStyle();
      } else {
        this.campuses = [];
      }
    },
    async removeCampus(index) {
      if (index) {
        try {
          await this.deleteFaq(index);
          if (this.school) {
            this.fetchBasicConfig(this.school);
          } else {
            this.fetchBasicConfig();
          }
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      }
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`,
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },
    async fetchBasicConfig(school_id = null) {
      let resp;
      if (school_id) {
        resp = await this.getFaqs({
          school_id: school_id,
        });
      } else {
        resp = await this.getFaqs({});
      }

      this.campuses = resp;
    },
    async updateOrder() {
      this.dragging = false;
      const data = {
        school:
          get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
            ? this.school
            : this.getLoggedInUser.linked_entity.id,
        faqs: map(this.campuses, (id, order_num) => ({
          id: id.id,
          order_num: order_num + 1,
        })),
      };
      try {
        await this.updateFaqsOrder(data);
        this.makeToast({ variant: 'success', msg: 'Order Successfully Updated!' });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchBasicConfig();
    },
  },

  async mounted() {
    this.isLoading = true;

    if (this.$route.query) {
      this.school = parseInt(this.$route.query.school_id);
      this.fetchBasicConfig(this.school);
      this.fetchPageStyle();
    }

    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.fetchSchools();
    }
    if (
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
    ) {
      (await this.getSchool(this.getLoggedInUser.linked_entity.id)).data;
    }
    try {
      if (
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
      ) {
        this.fetchBasicConfig();
        this.fetchPageStyle();
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
    }

    this.isLoading = false;
  },
};
</script>
